import React from 'react';
import SponsorLogos from '../SponsorLogos';
import { getScheduleDayDate } from '../../utils/eventUtils';
import { anchorIdForSlug } from '../../utils/scheduleAnchors';
import {
  SCHEDULE_CERTAINTY_PRELIMINARY,
  SCHEDULE_COLOR_CHARCOAL,
  SCHEDULE_COLOR_ORANGE,
  defaultAddOnCallout,
  defaultScheduleCta,
} from '../../constants/defaultSchedule';

function rowAnchorId(row) {
  const rowSlug = (row.stableSlug || '').trim().toLowerCase();
  return anchorIdForSlug(rowSlug) || `schedule-row-${row.id}`.replace(/[^a-zA-Z0-9-_]/g, '-');
}

function itemAnchorId(item) {
  const s = (item.stableSlug || '').trim().toLowerCase();
  return anchorIdForSlug(s) || `schedule-item-${item.id}`.replace(/[^a-zA-Z0-9-_]/g, '-');
}

function TimeCell({ rowOrItem }) {
  const generic = rowOrItem?.timeKind === 'generic';
  return (
    <div
      className={
        generic ? 'schedule-sheet-time schedule-sheet-time--generic' : 'schedule-sheet-time'
      }
    >
      {rowOrItem.time}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.event - Firestore event (for dates)
 * @param {object} props.scheduleData - migrated schedule fields
 * @param {Array} [props.sponsors]
 * @param {boolean} props.registrationOpen
 * @param {string} props.registrationUrl
 * @param {string} [props.fallbackInterestUrl] - when closed, optional link (e.g. marketing home)
 * @param {Set<string>} props.expandedPastDays
 * @param {(dayId: string) => void} props.togglePastDay
 * @param {boolean} [props.previewRegistrationOpen] - when set, overrides registrationOpen for preview
 */
export default function ScheduleStructuredLayout({
  event,
  scheduleData,
  sponsors = [],
  registrationOpen,
  registrationUrl,
  fallbackInterestUrl = '',
  expandedPastDays,
  togglePastDay,
  previewRegistrationOpen,
}) {
  const addOn = scheduleData.addOnCallout || defaultAddOnCallout();
  const cta = scheduleData.cta || defaultScheduleCta();
  const effectiveOpen =
    typeof previewRegistrationOpen === 'boolean' ? previewRegistrationOpen : registrationOpen;
  const regUrl = (registrationUrl || '').trim();
  const addLinkUrl = (addOn.linkUrl || '').trim() || regUrl;
  const addLinkLabel = (addOn.linkLabel || '').trim() || 'Register';
  const closedHref = (cta.interestListUrl || '').trim() || fallbackInterestUrl;

  const renderZebraRow = (key, timeEl, titleNode, noteNode, extraClass, anchorId) => (
    <div
      key={key}
      id={anchorId || undefined}
      className={`schedule-sheet-row${extraClass ? ` ${extraClass}` : ''}`}
    >
      {timeEl}
      <div className="schedule-sheet-cell-main">
        <div className="schedule-sheet-event">{titleNode}</div>
        {noteNode}
      </div>
    </div>
  );

  const renderNormalRow = (day, row, zebraKey) => {
    const aid = rowAnchorId(row);
    const title = row.emphasis ? <strong>{row.label}</strong> : row.label;
    const note = (row.note || '').trim() ? (
      <div className="schedule-sheet-note">{row.note}</div>
    ) : null;
    return renderZebraRow(
      zebraKey,
      <TimeCell rowOrItem={row} />,
      title,
      note,
      row.highlight ? 'schedule-sheet-row--highlight' : '',
      aid
    );
  };

  const renderItemRow = (item, zebraKey, anchorOverride) => {
    const aid = anchorOverride || itemAnchorId(item);
    const note = (item.note || '').trim() ? (
      <div className="schedule-sheet-note">{item.note}</div>
    ) : null;
    return renderZebraRow(zebraKey, <TimeCell rowOrItem={item} />, item.label, note, '', aid);
  };

  const renderRowBlock = (day, row, baseKey) => {
    if (row.type === 'normal') {
      return renderNormalRow(day, row, `${baseKey}-n`);
    }
    if (row.type === 'callout' && row.variant === 'fri') {
      const aid = rowAnchorId(row);
      const note = (row.note || '').trim() ? (
        <div className="schedule-sheet-note">{row.note}</div>
      ) : null;
      return renderZebraRow(
        `${baseKey}-fri`,
        <TimeCell rowOrItem={row} />,
        row.label,
        note,
        'schedule-sheet-row--callout schedule-sheet-row--callout-fri',
        aid
      );
    }
    if (row.type === 'main-block') {
      const aid = rowAnchorId(row);
      const parts = [];
      const items = row.items || [];
      const slugForBlock = (row.stableSlug || '').trim() ? aid : null;
      items.forEach((item, j) => {
        const blockAnchor = j === 0 && slugForBlock && !(row.note || '').trim() ? slugForBlock : null;
        parts.push(renderItemRow(item, `${baseKey}-mb-${j}`, blockAnchor));
      });
      if ((row.note || '').trim()) {
        parts.push(
          <div key={`${baseKey}-mb-note`} id={slugForBlock ? aid : undefined} className="schedule-sheet-row schedule-sheet-row--block-note">
            <div className="schedule-sheet-cell-full schedule-sheet-note">{row.note}</div>
          </div>
        );
      }
      return <React.Fragment key={baseKey}>{parts}</React.Fragment>;
    }
    if (row.type === 'callout' && row.variant === 'sat') {
      const parts = [];
      (row.items || []).forEach((item, j) => {
        parts.push(
          renderZebraRow(
            `${baseKey}-sat-${j}`,
            <TimeCell rowOrItem={item} />,
            item.label,
            (item.note || '').trim() ? (
              <div className="schedule-sheet-note">{item.note}</div>
            ) : null,
            'schedule-sheet-row--callout schedule-sheet-row--callout-sat',
            itemAnchorId(item)
          )
        );
      });
      return <React.Fragment key={baseKey}>{parts}</React.Fragment>;
    }
    return null;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPreliminary = scheduleData.scheduleCertainty === SCHEDULE_CERTAINTY_PRELIMINARY;

  return (
    <>
      {isPreliminary ? (
        <div className="schedule-sheet-preliminary-banner" role="status">
          <span className="schedule-sheet-preliminary-banner__label">Preliminary schedule</span>
          <span className="schedule-sheet-preliminary-banner__text">
            Times and activities are tentative and may change. Check back for updates.
          </span>
        </div>
      ) : null}
      <header className="schedule-sheet-hero">
        <div className="schedule-sheet-eyebrow">
          {(scheduleData.title || event?.title || 'Fresh Meet').trim()}
        </div>
        <h1 className="schedule-sheet-h1">
          {(scheduleData.heroTitle || 'Schedule').trim()}
        </h1>
        {(scheduleData.subtitle || '').trim() &&
        (scheduleData.subtitle || '').trim() !== (scheduleData.heroTitle || '').trim() ? (
          <p className="schedule-sheet-subdeck">{(scheduleData.subtitle || '').trim()}</p>
        ) : null}
        {(scheduleData.introBody || '').trim() ? (
          <p className="schedule-sheet-intro">{(scheduleData.introBody || '').trim()}</p>
        ) : null}
      </header>

      {effectiveOpen && (addOn.title || (addOn.body || '').trim() || addLinkUrl) && (
        <div className="schedule-sheet-addon">
          <span className="schedule-sheet-addon-title">{(addOn.title || 'Add-Ons').trim()} </span>
          {(addOn.body || '').trim() ? (
            <span className="schedule-sheet-addon-text"> — {(addOn.body || '').trim()} </span>
          ) : null}
          {addLinkUrl ? (
            <a
              className="schedule-sheet-addon-link"
              href={addLinkUrl}
              target="_blank"
              rel="noreferrer"
            >
              {addLinkLabel}
            </a>
          ) : (
            <span className="schedule-sheet-addon-text">{addLinkLabel}</span>
          )}
        </div>
      )}

      <div className="schedule-sheet-days">
        {scheduleData.days.map((day) => {
          const dayDate = getScheduleDayDate(event, day.id);
          const longDate =
            dayDate &&
            dayDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            });
          const isPast = dayDate && dayDate.getTime() < today.getTime();
          const isCollapsed = isPast && !expandedPastDays.has(day.id);
          const daySlug = (day.stableSlug || day.id || '').toString().trim().toLowerCase();
          const sectionId = anchorIdForSlug(daySlug) || `schedule-day-${day.id}`;
          const accent = (day.accentColor || '').trim() || (day.main ? SCHEDULE_COLOR_ORANGE : SCHEDULE_COLOR_CHARCOAL);
          const dayLabel = (day.dayLabel || day.cardSubtitle || '').trim();

          const headerInner = (
            <>
              <span className="schedule-sheet-day-date">{longDate || day.header}</span>
              {dayLabel ? (
                <span className="schedule-sheet-day-label" style={{ color: accent }}>
                  {dayLabel}
                </span>
              ) : null}
            </>
          );

          return (
            <article
              key={day.id}
              id={sectionId}
              className={`schedule-sheet-day${day.main ? ' schedule-sheet-day--main' : ''}${isCollapsed ? ' schedule-sheet-day--collapsed' : ''}`}
            >
              <div className="schedule-sheet-day-card">
                {isPast ? (
                  <button
                    type="button"
                    className="schedule-sheet-day-cap schedule-sheet-day-cap--toggle"
                    onClick={() => togglePastDay(day.id)}
                    aria-expanded={!isCollapsed}
                  >
                    {headerInner}
                    <span className="schedule-sheet-day-chevron" aria-hidden>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                  </button>
                ) : (
                  <div className="schedule-sheet-day-cap">{headerInner}</div>
                )}
                {(!isPast || expandedPastDays.has(day.id)) && (
                  <div className="schedule-sheet-rows">
                    {day.rows.map((row, ri) => renderRowBlock(day, row, `${day.id}-${row.id}-${ri}`))}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {Array.isArray(scheduleData.allWeekLines) && scheduleData.allWeekLines.length > 0 && (
        <section
          id={
            anchorIdForSlug((scheduleData.allWeekSlug || '').trim().toLowerCase()) ||
            'schedule-all-week-long'
          }
          className="schedule-mkt-allweek schedule-sheet-allweek"
          aria-label="All week"
        >
          <h2 className="schedule-mkt-allweek-title">All week long</h2>
          <ul className="schedule-mkt-allweek-list">
            {scheduleData.allWeekLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>
      )}

      {(scheduleData.footerNote || '').trim() ? (
        <p className="schedule-sheet-footer-note">{(scheduleData.footerNote || '').trim()}</p>
      ) : null}

      <div className="schedule-sheet-cta">
        <div className="schedule-sheet-cta-eyebrow">{(cta.headline || 'Secure Your Spot').trim()}</div>
        {effectiveOpen && regUrl ? (
          <a className="schedule-sheet-cta-btn" href={regUrl} target="_blank" rel="noreferrer">
            {(cta.buttonLabel || 'Register Now').trim()}
          </a>
        ) : closedHref ? (
          <a className="schedule-sheet-cta-btn" href={closedHref}>
            {(cta.closedButtonLabel || cta.buttonLabel || 'Register Now').trim()}
          </a>
        ) : (
          <span className="schedule-sheet-cta-muted">
            {(cta.closedButtonLabel || 'Registration link coming soon').trim()}
          </span>
        )}
      </div>

      {sponsors.length > 0 && (
        <section className="schedule-sponsor-block schedule-sponsor-block--footer" aria-label="Sponsors">
          <div className="schedule-sponsor-block__cap">Sponsors</div>
          <div className="schedule-sponsor-block__body">
            <SponsorLogos sponsors={sponsors} variant="subtle" />
          </div>
        </section>
      )}
    </>
  );
}
